import { Icon, Text, Flex, List, ListItem } from '@chakra-ui/react';
import { faBug, faCloudDownload } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Button, ButtonText, Modal } from '@morressier/alembic';
import { MailToLink } from '../Submission/MailToLink';
import { IFileUploaded } from './common/types';
import { useDraftResponse } from 'src/contexts/uploadDraftProvider';
import Divider from '../Divider';
import { useCommandResponse } from 'src/contexts/SourceFileCommandResponseContex';
import { openFile } from 'src/utils/file';

export interface PdfValidationResultError {
  code: string;
  message: string;
  status: number;
}

function groupLatexErrors(errors: string[]): Record<string, string[]> {
  const groupedErrors: Record<string, string[]> = {};

  errors.forEach(error => {
    // Remove dynamic filename and line numbers (e.g., "anyfile.tex:xx: ")
    const cleanedError = error
      .replace(/[\w-]+\.tex:\d+:\s*/g, '') // Removes <filename>.tex:xx:
      .replace(/LaTeX Warning: /g, ''); // Removes "LaTeX Warning: "

    let errorType = '';

    if (/Citation `([^`]+)/.test(cleanedError)) {
      errorType = 'citation_warning';
    } else if (
      /Package pdftex\.def Error: File `([^`]+)/.test(cleanedError) ||
      /LaTeX Error: File `([\w\d\-_.]+)'/.test(cleanedError)
    ) {
      errorType = 'missing_file';
    } else {
      errorType = 'other';
    }
    // Group errors by type
    let errorMessage = cleanedError;
    if (errorType === 'missing_file') {
      const fileMatch = cleanedError.match(/File `([^`]+)' /);
      errorMessage = fileMatch?.[1] ?? cleanedError;
    }
    if (!groupedErrors[errorType]) {
      groupedErrors[errorType] = [];
    }
    groupedErrors[errorType]!.push(errorMessage);
  });
  return groupedErrors;
}

const errorMap = {
  citation_warning: {
    title: 'Citation Error',
    subtitle:
      'Discrepancy between the citations in the main file and the bibliography file. Make sure all citations in the manuscript are included in the bibliography file.'
  },
  missing_file: {
    title: 'Missing Files',
    subtitle:
      'Referenced files not found. Make sure that the submission includes all referenced files.'
  }
};

interface FileErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileList: IFileUploaded[];
}

export const FileErrorModal: React.FC<FileErrorModalProps> = ({
  isOpen,
  onClose,
  fileList
}) => {
  const draftContext = useDraftResponse();
  const commandContext = useCommandResponse();

  const draftResponse = draftContext?.draftResponse;
  const commandResponse = commandContext?.commandResponse;

  const draftError = draftResponse?.data?.message?.error ?? [];
  const draftWarnings = draftResponse?.data?.message?.warnings ?? [];

  const commandError = commandResponse?.data?.error ?? [];
  const commandWarnings = commandResponse?.data?.warnings ?? [];

  const errorList = draftResponse
    ? [...draftError, ...draftWarnings]
    : [...commandError, ...commandWarnings];

  const groupedErrors = groupLatexErrors(errorList);

  const errorsList = Object.keys(groupedErrors).filter(
    key => groupedErrors[key]!.length > 0
  );

  const AccordionItems = errorsList?.map(key => ({
    //@ts-ignore
    title: errorMap[key]?.title || key,
    //@ts-ignore
    subtitle: errorMap[key]?.subtitle,
    //@ts-ignore
    content: (
      <List as="ul">
        {groupedErrors[key]?.map((error, index) => (
          <ListItem key={index} as="li">
            <Text textStyle="textBody">
              <Icon as={FontAwesomeIcon} icon={faCircle} width="6px" mr="2" />{' '}
              {error}
            </Text>
          </ListItem>
        ))}
      </List>
    )
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="50rem"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex
        gap={4}
        flexDirection="column"
        alignItems="flex-start"
        width="100%"
        padding={8}
      >
        <FontAwesomeIcon fontSize={40} icon={faBug} />
        <Flex flexDirection="column">
          <Text textStyle="headingGroup" tabIndex={1} paddingBottom={7}>
            Errors in Source File Upload
          </Text>
          <Text textStyle="textBody" tabIndex={1}>
            Your submission has issues that need to be addressed. For help,
            contact <MailToLink />
          </Text>

          <Flex my={3} flexDirection="column">
            {Divider}
          </Flex>
          <Flex flexDirection="column">
            <Accordion
              chakraAccordionProps={{
                width: '100%',
                maxH: '300px',
                overflowY: 'auto',
                sx: {
                  '& > .chakra-accordion__item:not(:last-of-type)': {
                    borderBottom: '1px solid',
                    borderColor: 'neutralPrimaryBorder'
                  }
                }
              }}
              items={[...AccordionItems]}
            />
          </Flex>

          <>
            {fileList.length > 0 ? (
              <Flex direction="column" mt={4}>
                {fileList.map((file, index) => (
                  <ButtonText
                    key={index}
                    my={2}
                    mr="auto"
                    onClick={() => openFile(file.url)}
                    iconLeft={
                      <Icon
                        as={FontAwesomeIcon}
                        icon={faCloudDownload}
                        color="actionSecondaryContent"
                      />
                    }
                  >
                    <Text
                      maxWidth={'100%'}
                      whiteSpace="normal"
                      overflowWrap={'anywhere'}
                    >
                      Download {file?.name}
                    </Text>
                  </ButtonText>
                ))}
              </Flex>
            ) : (
              <Text> No Files available for download</Text>
            )}
          </>
        </Flex>

        <Flex justifyContent="flex-end" width="100%">
          <Button
            mr={3}
            variant={'secondary'}
            onClick={() => {
              onClose();
            }}
          >
            Close
          </Button>
          <Button
            mr={3}
            variant={'primary'}
            onClick={() => {
              onClose();
            }}
          >
            Re-Upload
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
